import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowDown, ArrowRight, Code2, Brain, Shield, TrendingUp,
  Hexagon, Cloud, Plug, Layers, Sparkles, Building2, Trophy,
  FileCheck, Key, Globe, ChevronRight, Quote, Telescope, Hammer, Rocket,
  Landmark, Smartphone, Bitcoin
} from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { CodeSnippet, TerminalSnippet } from "@/components/CodeSnippet";
import { AnimatedGridBackground } from "@/components/AnimatedGridBackground";
import {
  MorphingOrb, MagneticButton, TextReveal, ScrollProgress,
  MouseGradient, NumberTicker, ParallaxSection, MagneticSpotlightButton
} from "@/components/PremiumAnimations";
import SEOHead from "@/components/SEOHead";
import { caseStudies } from "@/data/caseStudies";
import { blogPosts } from "@/data/blog";

const techLogos = [
  "Anthropic Claude", "OpenAI", "AWS", "Stripe", "Vercel",
  "PostgreSQL", "React", "Next.js", "Python", "Go",
  "Docker", "Kubernetes", "MongoDB", "Redis", "Solana"
];

const services = [
  { icon: Code2, title: "Full-Stack Product Development", desc: "End-to-end product development. Backend, frontend, mobile. From concept to production.", slug: "full-stack-development" },
  { icon: Brain, title: "AI/ML Integration & Automation", desc: "RAG systems, AI chatbots, ML pipelines, embeddings, document processing, automated workflows.", slug: "ai-ml-integration" },
  { icon: Shield, title: "FinTech Infrastructure", desc: "Payment processing, compliance automation, banking APIs, KYC/AML, regulatory reporting.", slug: "fintech-infrastructure" },
  { icon: TrendingUp, title: "Trading Platform Engineering", desc: "Algorithmic execution, smart order routing, market data processing, risk management.", slug: "trading-platform-engineering" },
  { icon: Hexagon, title: "Web3 & Blockchain", desc: "Smart contracts, DeFi protocols, NFT platforms, DAO governance, wallet integrations.", slug: "web3-blockchain" },
  { icon: Cloud, title: "Cloud & DevOps", desc: "AWS/GCP architecture, Kubernetes, CI/CD pipelines, infrastructure as code, monitoring.", slug: "cloud-devops" },
  { icon: Plug, title: "API Platform Development", desc: "RESTful APIs, GraphQL, webhooks, API gateways, rate limiting, developer portals.", slug: "api-platform-development" },
  { icon: Layers, title: "SaaS Development", desc: "Multi-tenant architecture, subscription billing, user management, analytics, admin panels.", slug: "saas-development" },
];

const industries = [
  { icon: Landmark, title: "FinTech & Banking", slug: "fintech-banking" },
  { icon: Layers, title: "SaaS & Platforms", slug: "saas-platforms" },
  { icon: Brain, title: "AI & Automation", slug: "ai-automation" },
  { icon: Smartphone, title: "Mobile Apps", slug: "mobile-apps" },
  { icon: Cloud, title: "Enterprise", slug: "enterprise" },
  { icon: Bitcoin, title: "Web3 & Crypto", slug: "crypto-web3" },
];

const differentiators = [
  { icon: Sparkles, title: "AI-Native Engineering", desc: "Claude, Cursor, and Copilot are embedded in our daily workflow. Not a buzzword — it's how we actually build." },
  { icon: Building2, title: "FinTech Domain Knowledge", desc: "Deep experience with banking systems, payment infrastructure, and regulatory compliance from enterprise-level work." },
  { icon: Trophy, title: "Production-Grade Standards", desc: "Enterprise engineering discipline applied to startup timelines. Architecture-first, tested, documented." },
  { icon: FileCheck, title: "Fixed-Scope Pricing", desc: "No hourly billing surprises. Milestone-based payments with clear deliverables and timelines." },
  { icon: Key, title: "Full Ownership & IP Rights", desc: "You own 100% of code, designs, and IP. No vendor lock-in. Complete documentation and handoff." },
  { icon: Globe, title: "Startup-Speed Delivery", desc: "8-16 week delivery cycles. Weekly demos. You see progress every week, not just at launch." },
];

const testimonials = [];

const comparisonData = [
  { traditional: "6-12 month timelines", venury: "8-16 week delivery" },
  { traditional: "Hourly billing uncertainty", venury: "Fixed-scope milestone pricing" },
  { traditional: "Generic web dev teams", venury: "Domain-focused engineers" },
  { traditional: "AI as marketing buzzword", venury: "AI embedded in daily workflow" },
  { traditional: "Support ends at launch", venury: "3-month warranty included" },
  { traditional: "You manage multiple vendors", venury: "One team, full stack" },
];

const processSteps = [
  { icon: Telescope, title: "Discover", desc: "Requirements deep-dive, architecture design, risk assessment" },
  { icon: Hammer, title: "Build", desc: "Sprint-based development with weekly demos and continuous delivery" },
  { icon: Rocket, title: "Scale", desc: "Production launch, monitoring setup, knowledge transfer" },
];

const mainValue = {
  headline: "Fast. Without Shortcuts.",
  subhead: "We use AI to move 3x faster, not to cut corners. AI handles boilerplate while our engineers handle system complexity and architecture.",
  points: [
    "Claude, Cursor, and GitHub Copilot utilized in every project",
    "AI generates initial code while engineers refine and perfect",
    "3x faster delivery without sacrificing code quality or security",
    "Fixed scope milestone pricing provides complete financial clarity"
  ]
};

export default function HomePage() {
  const featuredProjects = caseStudies.slice(0, 6);
  const latestPosts = blogPosts.slice(0, 3);

  return (
    <main data-testid="home-page">
      <SEOHead
        title="Venury | AI-Augmented Product Development"
        description="Venury — AI-augmented development studio building products for funded startups. Full-stack development, AI integration, cloud infrastructure. 8-16 week delivery."
        url="https://venury.tech/"
      />
      <ScrollProgress />
      <MouseGradient />

      {/* ==================== HERO SECTION ==================== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        {/* Background image layer */}
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1550439062-609e1531270e?w=1600&q=40")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Animated grid on top of image */}
        <AnimatedGridBackground />
        <div className="absolute inset-0 overflow-hidden">
          <MorphingOrb
            color1="rgba(22, 160, 133, 0.15)"
            color2="rgba(72, 201, 176, 0.08)"
            size={600}
            duration={25}
            style={{ top: "5%", right: "0%" }}
          />
          <MorphingOrb
            color1="rgba(230, 126, 34, 0.10)"
            color2="rgba(211, 84, 0, 0.06)"
            size={400}
            duration={30}
            style={{ bottom: "10%", left: "5%" }}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 via-[#0B1116]/60 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
              >
                <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">
                  AI-Augmented Dev Studio
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0, 0, 0.2, 1] }}
                className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight"
              >
                <span className="gradient-text-orange">Production-Grade</span> Products at{" "}
                <span className="gradient-text-teal">Startup Speed</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl mt-8"
              >
                Full-stack engineering powered by AI workflows. Concept to production
                in 8-16 weeks. Fixed pricing. You own 100% of the code.
              </motion.p>

              {/* Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="flex flex-wrap gap-8 mt-10"
              >
                <div className="text-left">
                  <span className="font-mono text-xl font-bold text-teal-bright block">8-16 Wk</span>
                  <span className="font-mono text-[10px] text-slate-600 uppercase tracking-wider">Delivery</span>
                </div>
                <div className="text-left">
                  <span className="font-mono text-xl font-bold text-teal-bright block">3x Faster</span>
                  <span className="font-mono text-[10px] text-slate-600 uppercase tracking-wider">AI-Augmented Teams</span>
                </div>
                <div className="text-left">
                  <span className="font-mono text-xl font-bold text-teal-bright block">Fixed Scope</span>
                  <span className="font-mono text-[10px] text-slate-600 uppercase tracking-wider">Pricing</span>
                </div>
              </motion.div>

              {/* CTAs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0 }}
                className="flex flex-col sm:flex-row gap-4 mt-12"
              >
                <MagneticSpotlightButton
                  as={Link}
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-3 text-base !rounded px-8 h-14"
                  strength={0.2}
                >
                  <span>Schedule Free Consultation</span>
                  <ArrowUpRight size={16} />
                </MagneticSpotlightButton>
                <MagneticButton strength={0.15}>
                  <Link
                    to="/work"
                    data-testid="hero-cta-secondary"
                    className="inline-flex items-center gap-3 border border-slate-700 hover:border-white text-white font-semibold text-base px-8 py-4 rounded transition-colors duration-200"
                  >
                    See Our Work
                  </Link>
                </MagneticButton>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="lg:col-span-6 hidden lg:block"
            >
              <CodeSnippet className="max-h-[520px]" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <ArrowDown size={20} className="text-slate-600" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ==================== LOGO MARQUEE ==================== */}
      <section data-testid="logos-section" className="py-16 border-y border-white/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <p className="font-mono text-xs text-slate-600 uppercase tracking-widest">
            Building with best-in-class technology
          </p>
        </div>
        <div className="relative overflow-hidden">
          <div className="marquee-track">
            {[...techLogos, ...techLogos].map((logo, i) => (
              <div key={i} className="flex-shrink-0 px-8 py-3 mx-2 border border-white/5 rounded bg-navy-base/30">
                <span className="font-mono text-sm text-slate-400 whitespace-nowrap">{logo}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== VALUE PROPOSITION ==================== */}
      <section className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              {mainValue.headline}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-16">
              {mainValue.subhead}
            </p>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left" staggerDelay={0.1}>
            {mainValue.points.map((point, i) => (
              <StaggerItem key={i}>
                <div className="glass-card p-6 rounded-lg h-full border border-white/5">
                  <div className="w-10 h-10 rounded-full bg-teal-primary/10 flex items-center justify-center mb-4">
                    <Sparkles className="text-teal-bright" size={18} />
                  </div>
                  <p className="text-white text-sm font-medium">{point}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== FEATURED PROJECTS ==================== */}
      <section id="projects" data-testid="projects-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Portfolio</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                  Featured Work
                </h2>
              </div>
              <Link to="/work" className="inline-flex items-center gap-2 font-mono text-sm text-teal-bright hover:text-white transition-colors duration-200">
                View All Projects <ArrowUpRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
            {featuredProjects.map((project, i) => (
              <StaggerItem key={project.slug}>
                <Link
                  to={`/work/${project.slug}`}
                  data-testid={`project-card-${i}`}
                  className="block glass-card gradient-border rounded-lg overflow-hidden group h-full flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden flex-shrink-0">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded backdrop-blur-sm">
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-heading text-lg font-semibold text-white mb-2 group-hover:text-teal-bright transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-5 flex-1">{project.shortDesc}</p>
                    <div className="flex gap-6 pt-4 border-t border-white/5">
                      {project.metrics.map((m, j) => (
                        <div key={j}>
                          <span className="font-mono text-lg font-bold text-teal-bright block">{m.value}</span>
                          <span className="font-mono text-[10px] text-slate-600 uppercase tracking-wider">{m.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== SERVICES GRID ==================== */}
      <section data-testid="services-section" className="py-20 md:py-32 bg-[#080C10] aurora-background relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Capabilities</span>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight max-w-lg">
                What We Build
              </h2>
              <p className="text-slate-400 max-w-md text-base leading-relaxed">
                From MVPs to enterprise systems. AI-native engineering across the full stack.
              </p>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" staggerDelay={0.08}>
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <StaggerItem key={i}>
                  <Link to={`/services/${service.slug}`} data-testid={`service-card-${i}`} className="block glass-card rounded-lg p-6 group hover:border-teal-primary/30 transition-colors duration-300 h-full flex flex-col">
                    <div className="w-12 h-12 rounded-lg bg-teal-primary/10 flex items-center justify-center mb-5 group-hover:bg-teal-primary/20 transition-colors duration-300 flex-shrink-0">
                      <Icon size={22} className="text-teal-bright" />
                    </div>
                    <h3 className="font-heading text-base font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4 flex-1">{service.desc}</p>
                    <span className="inline-flex items-center gap-1 font-mono text-xs text-teal-bright uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      Learn more <ChevronRight size={12} />
                    </span>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== INDUSTRIES ==================== */}
      <section data-testid="industries-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Industries</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                  Industries We Serve
                </h2>
              </div>
              <Link to="/industries" className="inline-flex items-center gap-2 font-mono text-sm text-teal-bright hover:text-white transition-colors duration-200">
                Explore All <ArrowUpRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" staggerDelay={0.06}>
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <StaggerItem key={i}>
                  <Link
                    to={`/industries/${ind.slug}`}
                    data-testid={`industry-home-${i}`}
                    className="block p-5 rounded-lg border border-white/5 hover:border-teal-primary/30 transition-all duration-300 text-center group h-full flex flex-col items-center justify-start"
                  >
                    <div className="w-12 h-12 rounded-lg bg-teal-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-primary/20 transition-colors duration-300">
                      <Icon size={22} className="text-teal-bright" />
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-white group-hover:text-teal-bright transition-colors duration-200">
                      {ind.title}
                    </h3>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== CODE + TERMINAL ==================== */}
      <section data-testid="engineering-section" className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Engineering</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-4">
              How We Engineer
            </h2>
            <p className="text-slate-400 max-w-lg text-base leading-relaxed mb-16">
               Code reviews on every PR. Automated deployments. Production monitoring from day one.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1.5 rounded bg-[#DEA584]/15 text-[#DEA584] font-mono text-xs font-bold border border-[#DEA584]/20">Rust</span>
                <span className="font-mono text-xs text-slate-400">stream.rs</span>
              </div>
              <div className="rounded-xl border border-white/10 bg-[#1a1e2e]/80 backdrop-blur-sm overflow-hidden">
                <div className="p-5 font-mono text-[13px] leading-7">
                  <div><span className="text-purple-400">pub struct</span> <span className="text-teal-300">DataPipeline</span><span className="text-orange-300">&lt;T: Serialize&gt;</span> {"{"}</div>
                  <div className="pl-6"><span className="text-slate-300">buffer:</span> <span className="text-teal-300">Arc&lt;RwLock&lt;VecDeque&lt;T&gt;&gt;&gt;</span>,</div>
                  <div className="pl-6"><span className="text-slate-300">capacity:</span> <span className="text-orange-300">usize</span>,</div>
                  <div>{"}"}</div>
                  <div className="text-slate-600">&nbsp;</div>
                  <div><span className="text-purple-400">impl</span>&lt;T: Serialize&gt; DataPipeline&lt;T&gt; {"{"}</div>
                  <div className="pl-6"><span className="text-purple-400">pub async fn</span> <span className="text-teal-300">ingest</span>(&self, batch: Vec&lt;T&gt;) {"{"}</div>
                  <div className="pl-12"><span className="text-slate-300">let mut buf = self.buffer.write().await;</span></div>
                  <div className="pl-12"><span className="text-purple-400">for</span> item <span className="text-purple-400">in</span> batch {"{"}</div>
                  <div className="pl-16"><span className="text-slate-300">buf.push_back(item);</span></div>
                  <div className="pl-16"><span className="text-purple-400">if</span> buf.len() &gt; self.capacity {"{"}</div>
                  <div className="pl-20"><span className="text-[#A8E6CF]">self.flush(&mut buf).await;</span></div>
                  <div className="pl-16">{"}"}</div>
                  <div className="pl-12">{"}"}</div>
                  <div className="pl-6">{"}"}</div>
                  <div>{"}"}</div>
                </div>
              </div>
            </div>
            <ParallaxSection speed={0.05}>
              <TerminalSnippet />
            </ParallaxSection>
          </div>
        </div>
      </section>

      {/* ==================== OUR PROCESS ==================== */}
      <section data-testid="process-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Process</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                  Discover. Build. Scale.
                </h2>
              </div>
              <Link to="/approach" className="inline-flex items-center gap-2 font-mono text-sm text-orange-bright hover:text-white transition-colors duration-200">
                Full Process Details <ArrowUpRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerDelay={0.15}>
            {processSteps.map((step, i) => {
              const StepIcon = step.icon;
              return (
                <StaggerItem key={i}>
                  <div className="relative p-8 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300">
                    <span className="font-mono text-6xl font-bold text-white/[0.03] absolute top-4 right-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="w-14 h-14 rounded-xl bg-teal-primary/10 flex items-center justify-center mb-6">
                      <StepIcon size={26} className="text-teal-bright" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== COMPARISON ==================== */}
      <section data-testid="comparison-section" className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">The Difference</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              Why Clients Choose Venury
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="glass-card rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 text-center">
                <div className="px-6 py-4 border-b border-white/5 bg-red-500/5">
                  <span className="font-mono text-xs uppercase tracking-widest text-slate-600">Traditional Agencies</span>
                </div>
                <div className="px-6 py-4 border-b border-white/5 bg-teal-primary/5">
                  <span className="font-mono text-xs uppercase tracking-widest text-teal-bright">Venury</span>
                </div>
              </div>
              {comparisonData.map((row, i) => (
                <div key={i} className="grid grid-cols-2 comparison-row">
                  <div className="px-6 py-4 border-b border-white/5 text-sm text-slate-400">{row.traditional}</div>
                  <div className="px-6 py-4 border-b border-white/5 text-sm text-white font-medium">{row.venury}</div>
                </div>
              ))}
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-20" staggerDelay={0.1}>
            {differentiators.map((item, i) => {
              const Icon = item.icon;
              return (
                <StaggerItem key={i}>
                  <div data-testid={`differentiator-${i}`} className="p-6 rounded-lg border border-white/5 hover:border-teal-primary/20 transition-colors duration-300">
                    <Icon size={24} className="text-teal-bright mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>



      {/* ==================== BLOG PREVIEW ==================== */}
      <section data-testid="blog-section" className="py-20 md:py-32 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <span className="font-mono text-sm text-orange-bright uppercase tracking-widest block mb-4">Insights</span>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
                  From the Engineering Blog
                </h2>
              </div>
              <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-sm text-orange-bright hover:text-white transition-colors duration-200">
                All Articles <ArrowUpRight size={14} />
              </Link>
            </div>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6" staggerDelay={0.1}>
            {latestPosts.map((post, i) => (
              <StaggerItem key={post.slug}>
                <Link to={`/blog/${post.slug}`} data-testid={`blog-home-${i}`} className="block glass-card rounded-lg overflow-hidden group h-full flex flex-col">
                  <div className="relative h-44 overflow-hidden flex-shrink-0">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent" />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-teal-bright">{post.category}</span>
                    <h3 className="font-heading text-base font-semibold text-white mt-2 mb-2 group-hover:text-teal-bright transition-colors duration-300 leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>
                    <div className="flex items-center gap-2 mt-4 text-xs text-slate-600">
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ==================== STATS ==================== */}
      <section data-testid="stats-section" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Track Record</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-16">
              By The Numbers
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: 10, prefix: "", suffix: "+", label: "Services Offered", sublabel: "Full-stack capabilities" },
              { value: 3, prefix: "", suffix: "x", label: "Faster Delivery", sublabel: "AI-augmented development" },
              { value: 8, prefix: "", suffix: "-16wk", label: "To Production", sublabel: "Our delivery timeline" },
              { value: 100, prefix: "", suffix: "%", label: "Code Ownership", sublabel: "You own everything we build" },
            ].map((stat, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div data-testid={`stat-${i}`} className="text-left">
                  <div className="font-mono text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3">
                    <NumberTicker end={stat.value} prefix={stat.prefix} suffix={stat.suffix} value={stat.value} />
                  </div>
                  <p className="font-heading text-sm font-semibold text-teal-bright mb-1">{stat.label}</p>
                  <p className="text-xs text-slate-600">{stat.sublabel}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

    </main >
  );
}
